import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';

interface GamesSectionProps {
  onThemeChange: (theme: string) => void;
}

const GamesSection: React.FC<GamesSectionProps> = ({ onThemeChange }) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);

  const jokes = [
    "Why don't scientists trust atoms? Because they make up everything! 😄",
    "What do you call a sleeping bull? A bulldozer! 🐂💤",
    "Why did the math book look so sad? Because it had too many problems! 📚😢",
    "What do you call a dinosaur that crashes his car? Tyrannosaurus Wrecks! 🦕🚗",
    "Why don't eggs tell jokes? They'd crack each other up! 🥚😂"
  ];

  const playColorGame = () => {
    const themes = ['rainbow', 'sunset', 'ocean', 'forest'];
    const randomTheme = themes[Math.floor(Math.random() * themes.length)];
    onThemeChange(randomTheme);
    showGameMessage('🎨 Magical colors! Play again for more!');
  };

  const showJoke = () => {
    const randomJoke = jokes[Math.floor(Math.random() * jokes.length)];
    showGameMessage(randomJoke);
  };

  const countStars = () => {
    const starCount = Math.floor(Math.random() * 15) + 5;
    const stars = '⭐'.repeat(starCount);
    showGameMessage(`Count the stars! ${stars} There are ${starCount} magical stars!`);
  };

  const playMusic = () => {
    setShowConfetti(true);
    showGameMessage('🎵 Happy music is playing! Look at the confetti! 🎶');
    setTimeout(() => setShowConfetti(false), 3000);
  };

  const showGameMessage = (message: string) => {
    setCurrentMessage(message);
    setShowMessage(true);
    setTimeout(() => setShowMessage(false), 4000);
  };

  const games = [
    {
      icon: '🎨',
      title: 'Color Magic',
      description: 'Click to change the theme colors!',
      action: playColorGame
    },
    {
      icon: '😄',
      title: 'Giggle Box',
      description: 'Get a funny joke to make you smile!',
      action: showJoke
    },
    {
      icon: '⭐',
      title: 'Star Counter',
      description: 'Count the magical stars!',
      action: countStars
    },
    {
      icon: '🎵',
      title: 'Happy Sounds',
      description: 'Play cheerful sounds and see confetti!',
      action: playMusic
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.8 },
    visible: { opacity: 1, y: 0, scale: 1 }
  };

  return (
    <section className="games" id="games">
      {showConfetti && <Confetti />}
      
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Fun & Games
        </motion.h2>

        <motion.p
          className="section-subtitle"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Interactive fun just for you! 🎮
        </motion.p>

        <motion.div
          className="games-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {games.map((game, index) => (
            <motion.button
              key={index}
              className="game-card"
              variants={cardVariants}
              onClick={game.action}
              whileHover={{ 
                y: -10, 
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className="game-icon wiggle"
                whileHover={{ scale: 1.2, rotate: 15 }}
                transition={{ duration: 0.3 }}
              >
                {game.icon}
              </motion.div>
              <h3>{game.title}</h3>
              <p>{game.description}</p>
            </motion.button>
          ))}
        </motion.div>
      </div>

      {/* Game Message */}
      {showMessage && (
        <motion.div
          className="game-message"
          initial={{ opacity: 0, y: -50, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.8 }}
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #ff6b9d, #a855f7)',
            color: 'white',
            padding: '20px 30px',
            borderRadius: '25px',
            fontSize: '1.2rem',
            fontWeight: '600',
            textAlign: 'center',
            zIndex: 1001,
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            maxWidth: '90%',
            lineHeight: '1.4'
          }}
        >
          {currentMessage}
        </motion.div>
      )}
    </section>
  );
};

export default GamesSection;
